import type { ButtonHTMLAttributes, ReactNode } from 'react';

import { cn } from '@/lib/cn';

type Variant = 'primary' | 'dark' | 'outline' | 'ghost';
type Size = 'sm' | 'md' | 'lg';

const VARIANTS: Record<Variant, string> = {
  primary: 'bg-brand-500 text-white hover:bg-brand-600 shadow-card',
  dark: 'bg-ink text-white hover:bg-ink-soft',
  outline: 'border border-line bg-white text-ink hover:border-brand-300 hover:text-brand-600',
  ghost: 'text-ink-soft hover:bg-brand-50 hover:text-brand-700',
};

const SIZES: Record<Size, string> = {
  sm: 'h-11 px-3.5 text-sm sm:h-9',
  md: 'h-11 px-5 text-sm',
  lg: 'h-12 px-6 text-base',
};

/** The same sizes as a floor, for a label that may need a second line. */
const WRAPPING_SIZES: Record<Size, string> = {
  sm: 'min-h-11 px-3.5 py-2 text-sm sm:min-h-9',
  md: 'min-h-11 px-5 py-2 text-sm',
  lg: 'min-h-12 px-6 py-2 text-base',
};

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: Variant;
  size?: Size;
  children: ReactNode;
  /**
   * Let a long label wrap and the button grow with it, where a narrow column
   * (the editor's inspector) would otherwise push the text out of the border.
   */
  wrap?: boolean;
}

export function Button({
  variant = 'primary',
  size = 'md',
  wrap = false,
  className,
  children,
  ...props
}: ButtonProps) {
  return (
    <button
      className={cn(
        'inline-flex items-center justify-center gap-2 rounded-xl font-semibold',
        'transition-colors disabled:cursor-not-allowed disabled:opacity-60',
        VARIANTS[variant],
        wrap ? cn(WRAPPING_SIZES[size], 'text-center [&>svg]:shrink-0') : SIZES[size],
        className,
      )}
      {...props}
    >
      {children}
    </button>
  );
}
